<div class="space-y-6">
  @if(hasAccess()) {
    <div class="bg-white p-6 rounded-md shadow-sm">
      <div class="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <h1 class="text-2xl font-bold text-gray-800">{{ 'backOffice.loyaltyManagement.title' | translate }}</h1>
        
        <!-- Tab Switcher -->
        <div class="flex items-center gap-2 bg-slate-100 p-1 rounded-lg border">
          <button 
            (click)="switchView('rewards')"
            [class]="'px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ' + (currentView() === 'rewards' ? 'bg-emerald-600 text-white shadow' : 'text-gray-600 hover:bg-gray-200')">
            <i class="fa-solid fa-gifts mr-2"></i>{{ 'backOffice.loyaltyManagement.tabs.rewards' | translate }}
          </button>
          <button 
            (click)="switchView('tiers')"
            [class]="'px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ' + (currentView() === 'tiers' ? 'bg-emerald-600 text-white shadow' : 'text-gray-600 hover:bg-gray-200')">
            <i class="fa-solid fa-trophy mr-2"></i>{{ 'backOffice.loyaltyManagement.tabs.tiers' | translate }}
          </button>
        </div>

        <button (click)="openAddModal()" class="bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-700 transition-colors flex items-center gap-2 justify-center">
          <i class="fa-solid fa-plus"></i>
          <span>{{ (currentView() === 'rewards' ? 'backOffice.loyaltyManagement.addNewReward' : 'backOffice.loyaltyManagement.addNewTier') | translate }}</span>
        </button>
      </div>

      @switch (loadingState()) {
        @case ('loading') {
          <div class="flex flex-col items-center justify-center py-20 text-gray-500">
            <i class="fa-solid fa-spinner animate-spin text-4xl mb-4"></i>
            <p class="text-lg font-semibold">{{ 'backOffice.loyaltyManagement.loading' | translate }}</p>
          </div>
        }
        @case ('error') {
          <div class="bg-red-100 border-l-4 border-red-500 text-red-800 p-6 rounded-md my-4" role="alert">
            <p class="font-bold text-lg mb-2">{{ 'backOffice.productManagement.errorTitle' | translate }}</p>
            <pre class="bg-red-200/50 text-red-900 p-3 rounded text-sm font-mono whitespace-pre-wrap">{{ loadingError() }}</pre>
          </div>
        }
        @case ('loaded') {
          @switch (currentView()) {
            @case ('rewards') {
              <!-- Rewards Table -->
              <div class="overflow-x-auto">
                <table class="min-w-full bg-white">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ 'backOffice.loyaltyManagement.rewardsTable.name' | translate }}</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ 'backOffice.loyaltyManagement.rewardsTable.sku' | translate }}</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{{ 'backOffice.loyaltyManagement.rewardsTable.points' | translate }}</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{{ 'backOffice.loyaltyManagement.rewardsTable.cost' | translate }}</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ 'backOffice.loyaltyManagement.rewardsTable.status' | translate }}</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{{ 'backOffice.productManagement.table.actions' | translate }}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    @for (reward of rewards(); track reward.id) {
                      <tr>
                        <td class="px-4 py-2">
                          <img [ngSrc]="reward.imageUrl || 'https://picsum.photos/seed/placeholder/40'" [alt]="reward.name" width="40" height="40" class="w-10 h-10 rounded-md object-cover">
                        </td>
                        <td class="px-4 py-2 font-medium text-gray-900">{{ reward.name }}</td>
                        <td class="px-4 py-2 text-gray-700">{{ reward.sku }}</td>
                        <td class="px-4 py-2 text-right text-gray-900">{{ reward.points_required.toLocaleString() }}</td>
                        <td class="px-4 py-2 text-right text-gray-900">{{ reward.cost.toFixed(2) }}</td>
                        <td class="px-4 py-2">
                          @if (reward.active) {
                            <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Active</span>
                          } @else {
                            <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Inactive</span>
                          }
                        </td>
                        <td class="px-4 py-2 text-right text-sm font-medium">
                          <a (click)="openEditRewardModal(reward)" class="text-emerald-600 hover:text-emerald-900 cursor-pointer mr-4">{{ 'backOffice.productManagement.edit' | translate }}</a>
                          <a (click)="deleteReward(reward)" class="text-red-600 hover:text-red-900 cursor-pointer">{{ 'backOffice.productManagement.delete' | translate }}</a>
                        </td>
                      </tr>
                    } @empty {
                      <tr><td colspan="7" class="text-center py-10 text-gray-500">No rewards found.</td></tr>
                    }
                  </tbody>
                </table>
              </div>
            }
            @case ('tiers') {
              <!-- Tiers Table -->
              <div class="overflow-x-auto">
                <table class="min-w-full bg-white">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{{ 'backOffice.loyaltyManagement.tiersTable.icon' | translate }}</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ 'backOffice.loyaltyManagement.tiersTable.name' | translate }}</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{{ 'backOffice.loyaltyManagement.tiersTable.minPoints' | translate }}</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ 'backOffice.loyaltyManagement.tiersTable.benefits' | translate }}</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{{ 'backOffice.productManagement.table.actions' | translate }}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    @for (tier of tiers(); track tier.id) {
                      <tr>
                        <td class="px-4 py-2 text-center text-2xl">{{ tier.icon }}</td>
                        <td class="px-4 py-2 font-medium text-gray-900">{{ tier.name }}</td>
                        <td class="px-4 py-2 text-right text-gray-900 font-semibold">{{ tier.min_points.toLocaleString() }}</td>
                        <td class="px-4 py-2 text-gray-700 text-sm whitespace-normal">{{ tier.benefits }}</td>
                        <td class="px-4 py-2 text-right text-sm font-medium">
                          <a (click)="openEditTierModal(tier)" class="text-emerald-600 hover:text-emerald-900 cursor-pointer mr-4">{{ 'backOffice.productManagement.edit' | translate }}</a>
                          <a (click)="deleteTier(tier)" class="text-red-600 hover:text-red-900 cursor-pointer">{{ 'backOffice.productManagement.delete' | translate }}</a>
                        </td>
                      </tr>
                    } @empty {
                       <tr><td colspan="5" class="text-center py-10 text-gray-500">No loyalty tiers found.</td></tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          }
        }
      }
    </div>

    <app-reward-form-modal
      [isOpen]="isRewardModalOpen()"
      [reward]="editingReward()"
      (closeModal)="isRewardModalOpen.set(false)"
      (saveReward)="handleSaveReward($event)">
    </app-reward-form-modal>

    <app-tier-form-modal
      [isOpen]="isTierModalOpen()"
      [tier]="editingTier()"
      (closeModal)="isTierModalOpen.set(false)"
      (saveTier)="handleSaveTier($event)">
    </app-tier-form-modal>
  } @else {
    <div class="bg-white p-8 rounded-md shadow-sm text-center">
        <i class="fa-solid fa-lock text-4xl text-yellow-500 mb-4"></i>
        <h2 class="text-2xl font-bold text-gray-800">Loyalty & Promotions is a Pro Feature</h2>
        <p class="text-gray-600 mt-2 max-w-md mx-auto">This feature allows you to create customer rewards and tiers. Please upgrade your plan to enable this functionality.</p>
        <a routerLink="/admin/billing" class="mt-6 inline-block bg-emerald-600 text-white px-6 py-3 rounded-md hover:bg-emerald-700 font-semibold">
            Upgrade to Pro
        </a>
    </div>
  }
</div>
